import React from "react";
import { ArrowRight, ChevronDown, Star } from "lucide-react";
import { Badge } from "../../../components/ui/badge";
import { ButtonLink } from "../../../components/ui/button";
import { ImageWithFallback } from "../../../components/ui/ImageWithFallback";
import { Heading, Text } from "../../../components/ui/typography";
import type { PublishedBusinessData } from "../../../../types/business";
import type { ContractorSitePageProps } from "../types";
import {
  defaultHeroDescription,
  resolveHeroHeadlineParts,
} from "../heroCopyDisplay";
import { publicSiteShowsFullQuoteForm } from "../sections";
import { resolveHeroImageSource } from "../siteCustomization";

type HeroSectionProps = Pick<
  ContractorSitePageProps,
  "business" | "projectImages"
> & {
  scrollHintHref: string;
};

type HeroHeadlineBusiness = Pick<
  PublishedBusinessData,
  "tradeDisplayName" | "heroHeadline"
>;

function HeroHeadlineBlock({ business }: { business: HeroHeadlineBusiness }) {
  const parts = resolveHeroHeadlineParts(business);
  if (parts.kind === "default") {
    return (
      <>
        Quality {parts.tradeDisplayName}
        <br />
        <span className="text-accent">You Can Trust</span>
      </>
    );
  }
  if (parts.kind === "custom_one") {
    return <>{parts.line}</>;
  }
  return (
    <>
      {parts.firstLine}
      <br />
      <span className="text-accent">{parts.accentLine}</span>
    </>
  );
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  business,
  projectImages = [],
  scrollHintHref,
}) => {
  const description =
    business.heroDescription?.trim() || defaultHeroDescription(business);
  const showQuoteForm = publicSiteShowsFullQuoteForm(business);

  return (
    <section className="relative flex min-h-screen items-center overflow-hidden">
      <div className="absolute inset-0">
        <ImageWithFallback
          src={resolveHeroImageSource({
            tradeDisplayName: business.tradeDisplayName,
            heroImageMode: business.heroImageMode,
            heroProjectImageId: business.heroProjectImageId,
            heroBannerUrl: business.heroBannerUrl,
            projectImages,
          })}
          alt={`${business.businessName} project`}
          className="h-full w-full object-cover"
          width={1920}
          height={1080}
          priority
        />
        <div className="absolute inset-0 bg-linear-to-r from-background/95 via-background/82 to-background/60" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 py-32 sm:px-6 sm:py-40 lg:px-8">
        <div className="max-w-2xl">
          {business.testimonials.length > 0 && (
            <Badge
              className="mb-6 inline-flex gap-2 px-4 py-2"
              variant="on-dark"
            >
              <div className="flex">
                {[...Array(5)].map((_, index) => (
                  <Star
                    key={index}
                    className="h-4 w-4 fill-accent text-accent"
                  />
                ))}
              </div>
              <span className="text-sm font-medium text-foreground">
                Rated 5.0 by local clients
              </span>
            </Badge>
          )}

          {business.seoH1 ? (
            <>
              <Heading
                level={1}
                size="lg"
                className="mb-4 max-w-xl text-foreground"
              >
                {business.seoH1}
              </Heading>
              <p className="font-display mb-6 text-4xl font-semibold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
                <HeroHeadlineBlock business={business} />
              </p>
            </>
          ) : (
            <Heading level={1} size="display" className="mb-6 text-foreground">
              <HeroHeadlineBlock business={business} />
            </Heading>
          )}

          <Text className="mb-8 max-w-lg" tone="muted" size="lg">
            {description}
          </Text>

          <div className="mb-12 flex flex-col gap-4 sm:flex-row">
            <ButtonLink href="#contact" variant="default" size="lg">
              {showQuoteForm ? "Get Your Free Quote" : "Contact Us"}
              <ArrowRight className="h-5 w-5" />
            </ButtonLink>
            {business.projects.length > 0 ? (
              <ButtonLink href="#projects" variant="surface" size="lg">
                View Our Work
              </ButtonLink>
            ) : null}
          </div>
        </div>
      </div>

      <a
        href={scrollHintHref}
        aria-label="Continue to more on this page"
        className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-1 rounded-full px-4 py-2 text-foreground/90 no-underline outline-offset-4 transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <span
          className="text-xs font-semibold uppercase tracking-[0.2em]"
          aria-hidden
        >
          Learn More
        </span>
        <ChevronDown
          className="h-6 w-6 motion-safe:animate-bounce"
          aria-hidden
        />
      </a>
    </section>
  );
};
